<script setup lang="ts">
// The `score` widget — ports v3 `CustomDashboardScoreCards.vue` (258 ln).
//
// What the port removes, and why each one mattered:
//   • `v-html` at `:16` — the metric name was injected as raw markup from the column catalog.
//     08's `MetricLabel` renders the same label (logo + text) as elements.
//   • `formatFixed(getChange(metric))%` at `:45` — a `toFixed` string with a literal `%` glued on,
//     so the delta never localised. `useFormat().formatPercent` owns it now.
//   • its own `getReportData` call and hand-rolled loader flag — 08's `useReportData` owns the
//     request, the abort on unmount, and the four states.
//   • the change math at `:120-128`, which divided by `prev` with no zero/NaN guard and could
//     render `Infinity%`. 08's `calcChange` returns `null` for every degenerate case.
//
// The `rawMetrics` "static metrics" branch of the legacy component is NOT ported: it exists only
// for a Summary caller that passes pre-computed platform tiles, not for the dashboard `score`
// widget this file is (§3.1). The `accountWorkspaces` prop, by contrast, IS declared here — legacy
// read `props.accountWorkspaces` while never declaring it, so account-center score cards have been
// sending `undefined` and reading the wrong workspace set.
import { computed, ref, watch } from 'vue'

import type { DashboardWidget } from '../../models/widget.types'
import type { WidgetSheet } from '../../models/widgetExport.types'
import {
  widgetToReportSpec,
  widgetWorkspaceIds,
  type WidgetRenderContext,
} from '../../service/widgetSpec'
import WidgetSkeleton from '../skeletons/WidgetSkeleton.vue'

import {
  applyGa4OverrideTotal,
  calcChange,
  Ga4Indicator,
  mapColumns,
  MetricLabel,
  resolveGa4OverrideFields,
  useMetricCatalog,
  useReportCellFormatter,
  useReportData,
  type ReportColumn,
  type ReportRow,
  type ReportSpec,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert, Button } from '@/shared/ui'

/** Legacy's fold threshold (`DEFAULT_VISIBLE_CARDS`) — beyond it the rest sit behind "show more". */
const DEFAULT_VISIBLE_CARDS = 8

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget
    title?: string
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    targetCurrency?: string | null
    defaultPostData?: string
  }>(),
  {
    title: '',
    wsId: null,
    accountWorkspaces: () => [],
    targetCurrency: null,
    defaultPostData: '',
  },
)
const { t } = useI18n()
const format = useFormat()
const catalog = useMetricCatalog()
const { formatCell } = useReportCellFormatter()
const expanded = ref(false)

const context = computed<WidgetRenderContext>(() => ({
  defaultPostData: props.defaultPostData,
  wsId: props.wsId,
  accountWorkspaces: props.accountWorkspaces,
  targetCurrency: props.targetCurrency,
}))
const workspaceIds = computed(() => widgetWorkspaceIds(props.widget.wire, context.value))

/** One totals row is the whole request — a score card reads `total`, never the row set. */
const spec = computed<ReportSpec | null>(() =>
  widgetToReportSpec(props.widget, context.value, { first: 0, rows: 1 }),
)
const report = useReportData(spec, { workspaceIds })

const columns = computed<ReportColumn[]>(() =>
  mapColumns(props.widget.wire.setMetrics ?? [], { getColumn: catalog.getColumn }),
)
const visibleColumns = computed(() =>
  expanded.value ? columns.value : columns.value.slice(0, DEFAULT_VISIBLE_CARDS),
)
const hiddenCount = computed(() => Math.max(columns.value.length - DEFAULT_VISIBLE_CARDS, 0))

/**
 * The multi-workspace controller keeps its legacy envelope, so the merged totals live under
 * `combined`. Ported from `:174` — an account-center score card reads the wrong numbers without it.
 *
 * GA4 override fields (`ga4_total_override`, ORP-25673) take their total from `total_ga4`, or else
 * from the `data_ga4` days inside the selected range (v3 `applyGa4OverrideTotal`).
 */
const ga4Totals = computed(() => {
  const response = report.response.value
  const payload = response?.combined ?? response
  const total = payload?.total
  return applyGa4OverrideTotal(typeof total === 'object' && total !== null ? total : {}, {
    totalGa4: payload?.total_ga4,
    dataGa4: payload?.data_ga4,
    postData: spec.value?.defaultPostData ?? props.defaultPostData,
    fields: resolveGa4OverrideFields(payload, catalog.getColumn),
  })
})
const totals = computed<ReportRow>(() => ga4Totals.value.total)

function isGa4Override(column: ReportColumn): boolean {
  return ga4Totals.value.fields.includes(column.field)
}

function value(column: ReportColumn): string {
  return formatCell(totals.value[column.field], column, true)
}

function previousValue(column: ReportColumn): string {
  return formatCell(totals.value[`prev_${column.field}`], column, true)
}

function hasPrevious(column: ReportColumn): boolean {
  return totals.value[`prev_${column.field}`] !== undefined
}

/** A ratio, or `null` when the previous value is missing, zero, or non-numeric (08 owns the guard). */
function change(column: ReportColumn): number | null {
  return calcChange(totals.value, column.field)
}

function changeTone(column: ReportColumn): 'up' | 'down' | 'flat' {
  const ratio = change(column)
  if (ratio === null || ratio === 0) return 'flat'
  return ratio > 0 ? 'up' : 'down'
}

function changeLabel(column: ReportColumn): string {
  return format.formatPercent(change(column), { scale: 'ratio', maxFrac: 2, signDisplay: 'always' })
}

/** The export fan-out's leaf (§8): one sheet of the tiles as they are currently resolved. */
function downloadData(): WidgetSheet[] {
  if (columns.value.length === 0) return []
  return [{ name: props.title, data: [{ ...totals.value }], cols: [...columns.value] }]
}

// Collapse when the widget is reconfigured, so a shorter metric list cannot leave "show less" on.
watch(
  () => props.widget,
  () => {
    expanded.value = false
  },
)

defineExpose({ downloadData })
</script>

<template>
  <div class="dashboard-score p-sm">
    <WidgetSkeleton
      v-if="report.state.value === 'loading' || report.state.value === 'idle'"
      :label="title || t('dashboards.widget.untitled')"
      :rows="2"
    />
    <Alert v-else-if="report.state.value === 'error'" tone="warning">
      {{ t('reports.section.error') }}
      <Button size="sm" variant="outline" @click="report.reload">
        {{ t('reports.section.retry') }}
      </Button>
    </Alert>
    <template v-else>
      <div
        class="dashboard-score__grid grid grid-cols-[repeat(auto-fit,_minmax(15rem,_1fr))] gap-sm"
      >
        <article
          v-for="column in visibleColumns"
          :key="column.field"
          class="dashboard-score__tile flex flex-col gap-2xs p-sm border border-border rounded-lg bg-surface shadow-pop"
        >
          <header
            class="dashboard-score__head flex items-start justify-between gap-xs min-w-0 text-[0.8125rem] font-medium text-muted"
          >
            <Ga4Indicator v-if="isGa4Override(column)" :label="column.header" variant="scorecard">
              <MetricLabel :column="column" :size="20" />
            </Ga4Indicator>
            <MetricLabel v-else :column="column" :size="20" />
            <span
              v-if="change(column) !== null"
              class="dashboard-score__delta flex-none py-0.5 px-2xs rounded-full bg-surface-sunken font-numeric text-[0.75rem] font-semibold"
              :class="`is-${changeTone(column)}`"
            >
              {{ changeLabel(column) }}
            </span>
          </header>
          <p class="dashboard-score__value font-numeric text-[1.75rem] font-semibold text-primary">
            {{ value(column) }}
          </p>
          <p
            v-if="hasPrevious(column)"
            class="dashboard-score__previous text-[0.8125rem] text-muted"
          >
            {{ t('dashboards.widget.versusPrevious', { value: previousValue(column) }) }}
          </p>
        </article>
      </div>
      <div v-if="hiddenCount > 0" class="dashboard-score__more flex justify-center mt-sm">
        <Button size="sm" variant="ghost" @click="expanded = !expanded">
          {{
            expanded
              ? t('dashboards.widget.showLess')
              : t('dashboards.widget.showMore', { count: hiddenCount })
          }}
        </Button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.dashboard-score__delta.is-up {
  color: var(--ui-color-success);
}
.dashboard-score__delta.is-down {
  color: var(--ui-color-danger);
}
.dashboard-score__delta.is-flat {
  color: var(--ui-color-muted);
}
</style>
